import "./Filter.css";
import {
	EventFilterValue, FilterCategory,
	GameEventFilterResponse,
	GameEventFilterUtilityProps,
} from "../../../types";
import "./FilterStorage.css";
import { useEffect, useRef, useState } from "react";
import { Badge } from "ui-kit";
import { _ } from "i18n";
import Modal from "ui-kit/src/Modal";
import { Field, Form } from "react-final-form";
import { ValidationErrors } from "final-form";
import { useGameStore } from "../../../store";
import { useAttachStore } from "../../../attach-store";
import { mergeFiltersWithAttachLock } from "../../../attach-mode";

interface FilterStorageProps {
	isAllowed: boolean;
	gameEventFilters?: GameEventFilterUtilityProps;
}

interface FormValues {
	name: string;
}

export function FilterStorage({ isAllowed, gameEventFilters }: FilterStorageProps) {
	const {
		filterValues,
		setFilterValues,
		setFilteredFilterValues,
		eventFilter
	} = useGameStore();
	const { target: attachTarget } = useAttachStore();

	const [showDropdown, setShowDropdown] = useState<boolean>(false);
	const [deleteModalData, setDeleteModalData] = useState<GameEventFilterResponse | false>(false);
	const [createModalData, setCreateModalData] = useState<boolean>(false);
	const [overwriteModalData, setOverwriteModalData] = useState<FormValues | false>(false);

	if (!isAllowed) {
		return;
	}

	const ref = useRef(null);

	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			if (ref.current && !(ref.current as HTMLElement).contains(event.target as Node)) {
				setShowDropdown(false);
			}
		}
		document.addEventListener("mousedown", handleClickOutside, { passive: true });

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	const handleOnLoad = (filter: GameEventFilterResponse) => {
		let newFilterValues: EventFilterValue[] = [];

		filter.filters.forEach((fr) => {
			if ([FilterCategory.Team, FilterCategory.Player, FilterCategory.HighlightItem].includes(eventFilter.getFilterCategory(fr.key))) {
				return;
			}

			fr.value.forEach((v) => {
				newFilterValues = eventFilter.mutateFilterValues(newFilterValues, fr.key, v, false, true)
			})
		});

		if (attachTarget !== null) {
			newFilterValues = mergeFiltersWithAttachLock(eventFilter, newFilterValues, attachTarget);
			setFilteredFilterValues(newFilterValues);
		}

		setFilterValues(newFilterValues);
		setShowDropdown(false);
	}

	const validateCreation = (values: FormValues): ValidationErrors | Promise<ValidationErrors> => {
		const errors: any = {};

		if (!values.name) {
			errors.form = _("label.error.invalidForm");
		}

		return errors;
	}

	const handleTrySubmit = (values: FormValues) => {
		if (
			gameEventFilters?.data
			&& gameEventFilters?.data.filter((f) => f.name.toLowerCase() === values.name.toLowerCase()).length !== 0
		) {
			setOverwriteModalData(values);
			return;
		}

		handleSubmit(values);
	}

	const handleSubmit = (values: FormValues) => {
		gameEventFilters?.create(values.name, filterValues);
		setOverwriteModalData(false);
		setCreateModalData(false);
	}

	const handleDelete = () => {
		if (deleteModalData !== false) {
			gameEventFilters?.delete(deleteModalData.id);
		}
		setDeleteModalData(false);
	}

	const areFiltersSavable = eventFilter.areFilterValuesSavable(filterValues);

	return (
		<>
			<div className="filter-storage" ref={ref}>
				<Badge
					label={_("event.filter.button.savedFilters")}
					variant={showDropdown ? "primary" : "default"}
					outline={true}
					block={true}
					size="sm"
					disabled={!gameEventFilters?.data?.length}
					onClick={() => setShowDropdown(!showDropdown)}
				/>
				{showDropdown && !!gameEventFilters?.data?.length && <div className="filter-storage__dropdown">
					{gameEventFilters.data.map((filter: GameEventFilterResponse, i) => <div
						className="filter-storage__item"
						key={`filter-${i}`}
					>
						<div onClick={() => handleOnLoad(filter)}>{filter.name}</div>
						<span onClick={() => {
							setDeleteModalData(filter);
							setShowDropdown(false)
						}} className="filter-storage__delete icon icon-trash-new"></span>
					</div>)}
				</div>}
			</div>
			<div>
				<Badge
					label={_("event.filter.button.saveFilter")}
					variant="default"
					outline={true}
					block={true}
					size="sm"
					disabled={!areFiltersSavable}
					onClick={() => setCreateModalData(true)}
				/>
			</div>
			<Modal title={_("event.filter.create.title")} show={createModalData} size="sm" className="filter-storage-modal">
				<p className="text">{_("event.filter.create.text")}</p>
				<Form
					onSubmit={handleTrySubmit}
					validate={validateCreation}
					initialValues={{ name: "" }}
					render={({ handleSubmit, submitting, pristine, valid }) => (
						<form onSubmit={handleSubmit}>
							<Field name="name">
								{({ input, meta }) => (
									<input
										type="text"
										placeholder={_("event.filter.create.input.placeholder")}
										{...input}
									/>
								)}
							</Field>
							<div className="buttons">
								<Badge
									label={_("common.action.cancel")}
									variant="primary"
									outline={true}
									block={true}
									disabled={filterValues.length === 0}
									onClick={() => setCreateModalData(false)}
								/>
								<button type="submit" disabled={!valid}>
									<Badge
										label={_("common.action.save")}
										variant="primary"
										block={true}
										disabled={!valid}
										onClick={() => { }}
									/>
								</button>
							</div>
						</form>
					)}
				/>
				<Modal title={_("event.filter.overwrite.title")} show={overwriteModalData !== false} size="xs" className="filter-storage-modal">
					<div className="buttons">
						<Badge
							label={_("common.action.cancel")}
							variant="primary"
							outline={true}
							block={true}
							onClick={() => setOverwriteModalData(false)}
						/>
						<Badge
							label={_("common.action.overwrite")}
							variant="danger"
							block={true}
							onClick={() => handleSubmit(overwriteModalData as FormValues)}
						/>
					</div>
				</Modal>
			</Modal>
			<Modal title={_("event.filter.delete.title")} show={deleteModalData !== false} size="xs" className="filter-storage-modal">
				<div className="buttons">
					<Badge
						label={_("common.action.cancel")}
						variant="primary"
						outline={true}
						block={true}
						onClick={() => setDeleteModalData(false)}
					/>
					<Badge
						label={_("common.action.delete")}
						variant="danger"
						block={true}
						onClick={handleDelete}
					/>
				</div>
			</Modal>
		</>
	)
}
